import React, { useState, useEffect } from 'react';
import axios from 'axios';
import IconPicker from './IconPicker';
import { ServiceIcon } from '../../constants/serviceIcons';

const API = process.env.REACT_APP_API_URL || '/api';

export default function ServicesTab({ settings, t }) {
  // ALL state owned locally
  const [allServices, setAllServices] = useState([]);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({ name_fr: '', name_en: '', description_fr: '', description_en: '', category_fr: '', category_en: '', duration_minutes: '', price_min: '', price_max: '', deposit_amount: '', icon: '' });
  const [serviceSaving, setServiceSaving] = useState(false);
  const [newCategoryMode, setNewCategoryMode] = useState(false);
  const [serviceLangTab, setServiceLangTab] = useState('fr');
  const [translating, setTranslating] = useState(false);

  // Load services on mount
  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const res = await axios.get(`${API}/admin/services`);
      setAllServices(res.data);
    } catch (err) { /* ignore */ }
  };

  const existingCategories = [...new Set(allServices.map(s => s.category).filter(Boolean))].sort();
  const existingCategoriesFr = [...new Set(allServices.map(s => s.category_fr).filter(Boolean))].sort();
  const existingCategoriesEn = [...new Set(allServices.map(s => s.category_en).filter(Boolean))].sort();

  const openServiceModal = (service = null) => {
    setNewCategoryMode(false);
    setServiceLangTab('fr');
    if (service) {
      setEditingService(service);
      setServiceForm({
        name_fr: service.name_fr || '', name_en: service.name_en || '',
        description_fr: service.description_fr || '', description_en: service.description_en || '',
        category_fr: service.category_fr || '', category_en: service.category_en || '',
        duration_minutes: service.duration_minutes || '', price_min: service.price_min || '', price_max: service.price_max || '',
        deposit_amount: service.deposit_amount || '',
        icon: service.image_url || ''
      });
    } else {
      setEditingService(null);
      setServiceForm({ name_fr: '', name_en: '', description_fr: '', description_en: '', category_fr: '', category_en: '', duration_minutes: '', price_min: '', price_max: '', deposit_amount: '', icon: '' });
    }
    setShowServiceModal(true);
  };

  const saveService = async () => {
    setServiceSaving(true);
    try {
      const payload = { ...serviceForm, image_url: serviceForm.icon };
      delete payload.icon;
      if (editingService) {
        await axios.put(`${API}/admin/services/${editingService.id}`, { ...payload, is_active: editingService.is_active });
      } else {
        await axios.post(`${API}/admin/services`, payload);
      }
      setShowServiceModal(false);
      loadServices();
    } catch (err) {
      alert(t.admin.services.saveError);
    } finally {
      setServiceSaving(false);
    }
  };

  const autoTranslateService = async () => {
    setTranslating(true);
    try {
      const source = serviceLangTab;
      const target = source === 'fr' ? 'en' : 'fr';
      const fields = [
        { src: `name_${source}`, dst: `name_${target}` },
        { src: `description_${source}`, dst: `description_${target}` },
        { src: `category_${source}`, dst: `category_${target}` }
      ];
      // Collect non-empty source texts and track their field mappings
      const textsToTranslate = [];
      const fieldMap = [];
      for (let i = 0; i < fields.length; i++) {
        const val = serviceForm[fields[i].src];
        if (val) {
          textsToTranslate.push(val);
          fieldMap.push(i);
        }
      }
      if (textsToTranslate.length > 0) {
        const res = await axios.post(`${API}/admin/translate`, { texts: textsToTranslate, source_lang: source, target_lang: target });
        const translated = res.data.translated;
        const updates = {};
        for (let j = 0; j < fieldMap.length; j++) {
          updates[fields[fieldMap[j]].dst] = translated[j];
        }
        setServiceForm(prev => ({ ...prev, ...updates }));
      }
    } catch (err) {
      alert(err.response?.data?.error || t.admin.services.translateError);
    } finally {
      setTranslating(false);
    }
  };

  const reorderService = async (serviceId, direction) => {
    try {
      await axios.patch(`${API}/admin/services/${serviceId}/reorder`, { direction });
      loadServices();
    } catch (err) { /* ignore */ }
  };

  const toggleServiceActive = async (service) => {
    try {
      await axios.put(`${API}/admin/services/${service.id}`, {
        name_fr: service.name_fr, name_en: service.name_en,
        description_fr: service.description_fr, description_en: service.description_en,
        category_fr: service.category_fr, category_en: service.category_en,
        duration_minutes: service.duration_minutes, price_min: service.price_min, price_max: service.price_max,
        deposit_amount: service.deposit_amount,
        image_url: service.image_url, is_active: !service.is_active
      });
      loadServices();
    } catch (err) { /* ignore */ }
  };

  return (
    <>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2rem', color: '#D42B2B', margin: 0 }}>{t.admin.services.title}</h2>
          <button className="btn btn-primary" onClick={() => openServiceModal()}>
            {t.admin.services.addService}
          </button>
        </div>

        <div className="table-responsive">
          <table className="table table-dark table-hover">
            <thead>
            <tr>
              <th style={{ width: '60px' }}></th>
              <th style={{ width: '50px' }}>{t.admin.services.icon || 'Icon'}</th>
              <th>{t.admin.services.name}</th>
              <th>{t.admin.services.category}</th>
              <th>{t.admin.services.duration}</th>
              <th>{t.admin.services.priceMin}</th>
              <th>{t.admin.services.priceMax}</th>
              <th>{t.admin.services.status}</th>
              <th>{t.admin.tableHeaders.actions}</th>
            </tr>
            </thead>
            <tbody>
            {allServices.map((svc, idx) => (
                <tr key={svc.id}>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <button
                          disabled={idx === 0}
                          onClick={() => reorderService(svc.id, 'up')}
                          style={{ background: 'transparent', border: 'none', color: idx === 0 ? '#444' : '#D42B2B', cursor: idx === 0 ? 'default' : 'pointer', fontSize: '0.75rem', padding: '0 4px', lineHeight: 1 }}
                          aria-label="Move up"
                      >&#9650;</button>
                      <button
                          disabled={idx === allServices.length - 1}
                          onClick={() => reorderService(svc.id, 'down')}
                          style={{ background: 'transparent', border: 'none', color: idx === allServices.length - 1 ? '#444' : '#D42B2B', cursor: idx === allServices.length - 1 ? 'default' : 'pointer', fontSize: '0.75rem', padding: '0 4px', lineHeight: 1 }}
                          aria-label="Move down"
                      >&#9660;</button>
                    </div>
                  </td>
                  <td>{svc.image_url ? <ServiceIcon iconKey={svc.image_url} size={20} /> : <span style={{ color: '#64748B' }}>—</span>}</td>
                  <td>
                    <div style={{ color: '#E8ECF0' }}>{svc.name_fr || svc.name}{(!svc.description_fr?.trim() || !svc.description_en?.trim() || !svc.category_fr?.trim() || !svc.category_en?.trim()) && <span className="badge bg-warning text-dark ms-1" style={{ fontSize: '0.6rem' }}>&#9888; Translation</span>}</div>
                    <div style={{ color: '#64748B', fontSize: '0.75rem' }}>{svc.name_en || ''}</div>
                  </td>
                  <td>{svc.category_fr || svc.category}</td>
                  <td>{svc.duration_minutes} min</td>
                  <td>${svc.price_min}</td>
                  <td>${svc.price_max}</td>
                  <td>
                    <span className={`badge badge-status-${svc.is_active ? 'active' : 'inactive'}`} style={{ padding: '0.4rem 0.8rem', fontSize: '0.7rem' }}>
                      {svc.is_active ? t.admin.services.active : t.admin.services.inactive}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.3rem' }}>
                      <button className="btn btn-outline-primary btn-sm" onClick={() => openServiceModal(svc)} style={{ fontSize: '0.7rem' }}>
                        {t.common.edit}
                      </button>
                      <button
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => toggleServiceActive(svc)}
                          style={{ fontSize: '0.7rem' }}
                      >
                        {svc.is_active ? t.admin.services.inactive : t.admin.services.active}
                      </button>
                    </div>
                  </td>
                </tr>
            ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Service Modal (Bilingual) */}
      {showServiceModal && (
          <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editingService ? t.admin.services.editService : t.admin.services.addService}</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowServiceModal(false)}></button>
                </div>
                <div className="modal-body">
                  {/* FR/EN tab pills + auto-translate */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <ul className="nav nav-pills" style={{ gap: '0.5rem' }}>
                      <li className="nav-item">
                        <button className={`nav-link ${serviceLangTab === 'fr' ? 'active' : ''}`} onClick={() => setServiceLangTab('fr')}>{t.admin.services.langTabFr}</button>
                      </li>
                      <li className="nav-item">
                        <button className={`nav-link ${serviceLangTab === 'en' ? 'active' : ''}`} onClick={() => setServiceLangTab('en')}>{t.admin.services.langTabEn}</button>
                      </li>
                    </ul>
                    <button className="btn btn-outline-primary btn-sm" onClick={autoTranslateService} disabled={translating}>
                      {translating ? t.admin.services.translating : t.admin.services.autoTranslate}
                    </button>
                  </div>

                  {/* Language-specific fields */}
                  {serviceLangTab === 'fr' && (
                    <>
                      <div className="mb-3">
                        <label className="form-label">{t.admin.services.nameFr}</label>
                        <input className="form-control" value={serviceForm.name_fr} onChange={e => setServiceForm({ ...serviceForm, name_fr: e.target.value })} />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">{t.admin.services.descriptionFr}</label>
                        <textarea className="form-control" rows={3} value={serviceForm.description_fr} onChange={e => setServiceForm({ ...serviceForm, description_fr: e.target.value })} />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">{t.admin.services.categoryFr}</label>
                        {!newCategoryMode ? (
                          <select className="form-select" value={serviceForm.category_fr} onChange={e => {
                            if (e.target.value === '__new__') {
                              setNewCategoryMode(true);
                              setServiceForm({ ...serviceForm, category_fr: '' });
                            } else {
                              setServiceForm({ ...serviceForm, category_fr: e.target.value });
                            }
                          }}>
                            <option value="">{t.admin.services.selectCategory}</option>
                            {existingCategoriesFr.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                            <option value="__new__">{t.admin.services.addNewCategory}</option>
                          </select>
                        ) : (
                          <div className="input-group">
                            <input className="form-control" placeholder={t.admin.services.newCategoryPlaceholder} value={serviceForm.category_fr} onChange={e => setServiceForm({ ...serviceForm, category_fr: e.target.value })} autoFocus />
                            <button className="btn btn-outline-secondary" type="button" aria-label="Clear" onClick={() => {
                              setNewCategoryMode(false);
                              setServiceForm({ ...serviceForm, category_fr: '' });
                            }}>&times;</button>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {serviceLangTab === 'en' && (
                    <>
                      <div className="mb-3">
                        <label className="form-label">{t.admin.services.nameEn}</label>
                        <input className="form-control" value={serviceForm.name_en} onChange={e => setServiceForm({ ...serviceForm, name_en: e.target.value })} />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">{t.admin.services.descriptionEn}</label>
                        <textarea className="form-control" rows={3} value={serviceForm.description_en} onChange={e => setServiceForm({ ...serviceForm, description_en: e.target.value })} />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">{t.admin.services.categoryEn}</label>
                        {!newCategoryMode ? (
                          <select className="form-select" value={serviceForm.category_en} onChange={e => {
                            if (e.target.value === '__new__') {
                              setNewCategoryMode(true);
                              setServiceForm({ ...serviceForm, category_en: '' });
                            } else {
                              setServiceForm({ ...serviceForm, category_en: e.target.value });
                            }
                          }}>
                            <option value="">{t.admin.services.selectCategory}</option>
                            {existingCategoriesEn.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                            <option value="__new__">{t.admin.services.addNewCategory}</option>
                          </select>
                        ) : (
                          <div className="input-group">
                            <input className="form-control" placeholder={t.admin.services.newCategoryPlaceholder} value={serviceForm.category_en} onChange={e => setServiceForm({ ...serviceForm, category_en: e.target.value })} autoFocus />
                            <button className="btn btn-outline-secondary" type="button" aria-label="Clear" onClick={() => {
                              setNewCategoryMode(false);
                              setServiceForm({ ...serviceForm, category_en: '' });
                            }}>&times;</button>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* Shared fields (below tabs) */}
                  <hr />
                  <div className="mb-3">
                    <IconPicker value={serviceForm.icon} onChange={icon => setServiceForm({ ...serviceForm, icon })} />
                  </div>
                  <div className="row mb-3">
                    <div className="col-4">
                      <label className="form-label">{t.admin.services.duration}</label>
                      <input type="number" className="form-control" value={serviceForm.duration_minutes} onChange={e => setServiceForm({ ...serviceForm, duration_minutes: e.target.value })} />
                    </div>
                    <div className="col-4">
                      <label className="form-label">{t.admin.services.priceMin}</label>
                      <input type="number" step="0.01" className="form-control" value={serviceForm.price_min} onChange={e => setServiceForm({ ...serviceForm, price_min: e.target.value })} />
                    </div>
                    <div className="col-4">
                      <label className="form-label">{t.admin.services.priceMax}</label>
                      <input type="number" step="0.01" className="form-control" value={serviceForm.price_max} onChange={e => setServiceForm({ ...serviceForm, price_max: e.target.value })} />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-12 col-md-4 mb-3">
                      <label className="form-label">{t.admin.depositAmount}</label>
                      <input
                        type="number"
                        className="form-control"
                        step="0.01"
                        min="0"
                        value={serviceForm.deposit_amount || ''}
                        onChange={e => setServiceForm({ ...serviceForm, deposit_amount: e.target.value })}
                        placeholder="0.00"
                      />
                      <div className="form-text">{t.admin.depositAmountHelp}</div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-outline-primary" onClick={() => setShowServiceModal(false)}>{t.common.cancel}</button>
                  <button className="btn btn-primary" onClick={saveService} disabled={serviceSaving || !serviceForm.name_fr?.trim() || !serviceForm.name_en?.trim() || !serviceForm.description_fr?.trim() || !serviceForm.description_en?.trim() || !serviceForm.category_fr?.trim() || !serviceForm.category_en?.trim()}>
                    {serviceSaving ? t.admin.services.saving : t.admin.services.saveService}
                  </button>
                </div>
              </div>
            </div>
          </div>
      )}
    </>
  );
}
